import {
  AdminHeader,
  AdminInput,
  AdminSpinner,
  Box,
  Button,
  Div,
  EditPenIconRed,
  ErrorToast,
  H5,
  Icon,
  ListSpinner,
  ModalPortal,
  P,
  QueryStatus,
  SearchInput,
  SuccessToast,
  agentImage,
  handleChangeNumInputNoDecimalsMinMax,
  handleError,
  updateAgentReferralPercentage,
  handleSetUrlSearchParams,
} from '@vgrubs/components';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import { useAgentChildrenInfiniteScroll } from './hooks/useAgentChildrenInfiniteScroll';

export const AgentList = () => {
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('query') || '';
  const navigate = useNavigate();
  const [percentage, setPercentage] = useState<number | null>(null);

  const [updateReferralPercentageModal, setUpdateReferralPercentageModal] =
    useState<{ id: string | number; percentage: number } | null>(null);

  const { id } = useParams() as { id: string };

  const {
    agents,
    inputValue,
    handleInputChange,
    observerRef,
    isFetchingNextPage,
    status,
    refetch,
  } = useAgentChildrenInfiniteScroll({ agentId: id, initialQuery });

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(e);
    handleSetUrlSearchParams('query', e.target.value, setSearchParams, true);
  };

  const handleOnAgent = (id) => {
    navigate(`/users/agent/${id}`);
  };

  const handleUpdateReferralPercentage = async () => {
    if (!percentage) return ErrorToast('Percentage is required');
    if (percentage > 100)
      return ErrorToast('Percentage cannot be greater than 100');
    if (percentage < 1) return ErrorToast('Percentage cannot be less than 1');
    setLoading(true);
    try {
      const { data } = await updateAgentReferralPercentage(
        updateReferralPercentageModal?.id || '',
        Number(percentage)
      );
      SuccessToast(data.message);
      setUpdateReferralPercentageModal(null);
      refetch();
    } catch (error) {
      ErrorToast(handleError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {(status === QueryStatus.PENDING || loading) && <AdminSpinner />}
      <AdminHeader title={'Agent List'}>
        <SearchInput
          value={inputValue}
          maxWidth="350px"
          maxWidthSM="120px"
          placeholder="Search"
          onChange={onSearchChange}
        />
      </AdminHeader>
      <Div $mt="32"></Div>

      {agents.map(({ firstName, lastName, percentage, id }, i) => {
        return (
          <Box
            key={i}
            $flex
            $alignCenter
            $between
            $mb="8"
            $pointer
            onClick={() => handleOnAgent(id)}
          >
            <H5 $mb="0">
              {firstName} {lastName}
            </H5>
            <Div $flex $center $gap="8px">
              <P $m="0">
                Percentage Deal:
                <span> {percentage || 0}%</span>
              </P>
              <Icon
                $size="32"
                $pointer
                onClick={(e) => {
                  e.stopPropagation();
                  setUpdateReferralPercentageModal({
                    id,
                    percentage: percentage || 0,
                  });
                  setPercentage(percentage || 0);
                }}
              >
                <EditPenIconRed />
              </Icon>
            </Div>
          </Box>
        );
      })}

      {agents.length === 0 &&
        status !== QueryStatus.PENDING &&
        !isFetchingNextPage &&
        !loading && (
          <NoDataComponent
            image={agentImage}
            title="No Agents"
            description="No agents found for the selected filters."
          />
        )}

      <ListSpinner loading={isFetchingNextPage} observerRef={observerRef} />

      <ModalPortal
        isOpen={!!updateReferralPercentageModal}
        closeModal={() => setUpdateReferralPercentageModal(null)}
        header="Update Percentage Deal"
      >
        <AdminInput
          label="Percentage Deal"
          placeholder="Enter here"
          value={percentage ?? ''}
          $textCenter
          onChange={(e) =>
            handleChangeNumInputNoDecimalsMinMax(e, setPercentage, 0, 100)
          }
        />
        <Button
          $w="100%"
          $mt="32"
          $selected
          onClick={handleUpdateReferralPercentage}
        >
          Save
        </Button>
        <Button
          $w="100%"
          $mt="8"
          $selected
          $gray
          onClick={() => setUpdateReferralPercentageModal(null)}
        >
          Cancel
        </Button>
      </ModalPortal>
    </>
  );
};
